// utils/apiFetch.js
import { handleRedirect } from "./handleRedirect";

const DEFAULT_TIMEOUT_MS = 30000;

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
  }
}

/**
 * Production-grade fetch wrapper.
 * - Attaches auth token
 * - Handles timeouts via AbortController
 * - Normalizes errors
 * - Handles 401/403 via centralized redirect
 * - Safely parses JSON (handles empty responses)
 * - Does NOT add cache-busting query params (avoids CORS preflight issues)
 *
 * Cache control should be enforced server-side via response headers,
 * not client-side request headers (which trigger CORS preflight).
 */
export async function fetchWrapper(url, options = {}) {
  const {
    timeout = DEFAULT_TIMEOUT_MS,
    skipAuth = false,
    signal: externalSignal,
    ...fetchOptions
  } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Chain external signal with our timeout signal
  if (externalSignal) {
    if (externalSignal.aborted) {
      controller.abort();
    } else {
      externalSignal.addEventListener("abort", () => controller.abort(), { once: true });
    }
  }

  try {
    const headers = { ...(fetchOptions.headers || {}) };

    if (!skipAuth) {
      const token = localStorage.getItem("authToken");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      ...fetchOptions,
      headers,
  cache: "no-store",
      signal: controller.signal,
    });

    // Handle auth failures centrally
    if (response.status === 401 || response.status === 403) {
      handleRedirect(response.status);
      throw new ApiError("Unauthorized", response.status, null);
    }

    // Safely parse response body (may be empty for 204 No Content)
    let data = null;
    const contentType = response.headers.get("content-type") || "";
    if (response.status !== 204 && contentType.includes("application/json")) {
      try {
        data = await response.json();
      } catch {
        data = null;
      }
    }

    if (!response.ok) {
      handleRedirect(response.status);
      throw new ApiError(
        data?.message || `Request failed with status ${response.status}`,
        response.status,
        data
      );
    }

    return data;
  } catch (err) {
    if (err.name === "AbortError") {
      throw new ApiError("Request timeout", 408, null);
    }
    if (err instanceof ApiError) {
      throw err;
    }
    // Network error, CORS, DNS failure, etc.
    throw new ApiError(err.message || "Network error", 0, null);
  } finally {
    clearTimeout(timeoutId);
  }
}

export { ApiError };